import { useEffect } from 'react';
import { useDomainConfig } from './useDomainConfig';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export const useGoogleAnalytics = () => {
  const config = useDomainConfig();

  useEffect(() => {
    const { analytics } = config;
    
    // Only initialize if tracking is enabled and we have a GA ID
    if (!analytics.trackingEnabled || !analytics.googleAnalyticsId) {
      return;
    }

    // Check if script is already loaded
    const existingScript = document.querySelector(
      `script[src*="googletagmanager.com/gtag/js?id=${analytics.googleAnalyticsId}"]`
    );

    if (existingScript) {
      return; // Already loaded
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];

    // Define gtag function
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };

    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${analytics.googleAnalyticsId}`;
    document.head.appendChild(script);

    script.onload = () => {
      // Initialize Google Analytics
      window.gtag('js', new Date());
      window.gtag('config', analytics.googleAnalyticsId, {
        send_page_view: true,
        anonymize_ip: true, // For privacy compliance
        cookie_flags: 'SameSite=None;Secure'
      });
    };

    // Cleanup function
    return () => {
      // Remove the script on cleanup
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [config.analytics.googleAnalyticsId, config.analytics.trackingEnabled]);

  // Helper functions for tracking events
  const trackEvent = (eventName: string, parameters: Record<string, any> = {}) => {
    if (window.gtag && config.analytics.trackingEnabled) {
      window.gtag('event', eventName, parameters);
    }
  };

  const trackVideoDownload = (platform: string, videoTitle: string, format: string) => {
    trackEvent('video_download', {
      event_category: 'Download',
      event_label: platform,
      video_title: videoTitle,
      format: format,
      value: 1
    });
  };

  const trackVideoSearch = (url: string) => {
    let platform = 'unknown';
    if (url.includes('youtube.com') || url.includes('youtu.be')) platform = 'youtube';
    else if (url.includes('tiktok.com')) platform = 'tiktok';
    else if (url.includes('twitter.com') || url.includes('x.com')) platform = 'twitter';
    else if (url.includes('facebook.com')) platform = 'facebook';
    else if (url.includes('instagram.com')) platform = 'instagram';
    else if (url.includes('vimeo.com')) platform = 'vimeo';
    else if (url.includes('bilibili.com')) platform = 'bilibili';
    else if (url.includes('xiaohongshu.com') || url.includes('xhscdn.com')) platform = 'xiaohongshu';
    
    trackEvent('video_search', {
      event_category: 'Search',
      event_label: platform,
      search_term: url,
      value: 1
    });
  };

  const trackPageView = (pagePath: string, pageTitle: string) => {
    if (window.gtag && config.analytics.trackingEnabled) {
      window.gtag('config', config.analytics.googleAnalyticsId, {
        page_path: pagePath,
        page_title: pageTitle
      });
    }
  };

  return {
    trackEvent,
    trackVideoDownload,
    trackVideoSearch,
    trackPageView,
    isTrackingEnabled: config.analytics.trackingEnabled
  };
}; 